/**
 * Enhanced TopNavLayout with Submenu Support
 * Redesigned navigation with dropdown menu for 시스템관리
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  useTheme,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Breadcrumbs,
  Link,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLogout } from '@refinedev/core';
import { Header } from './Header';
import { Footer } from './Footer';
import { MainMenu } from './MainMenu';
import { navigationConfig } from '../../config/navigation';
import { MenuItem, Breadcrumb } from '../../types/navigation';
import { usePermission } from '../../hooks/usePermission';
import { useAuthValidation } from '../../hooks/useAuthValidation';
import { authService } from '../../services/auth.service';
import { SessionExpiredDialog } from '../SessionExpiredDialog';

/**
 * Enhanced layout props
 */
export interface TopNavLayoutEnhancedProps {
  /** Child components */
  children: React.ReactNode;
  /** User information */
  userInfo?: {
    name: string;
    full_name?: string;
    role?: string;
  };
  /** Whether to show breadcrumbs */
  showBreadcrumbs?: boolean;
}

/**
 * Dream TMS Enhanced Navigation Layout
 * Features:
 * - Horizontal navigation with 7 main items
 * - Dropdown submenu for 시스템관리
 * - Hover and click interaction
 * - Mobile responsive drawer
 * - Breadcrumb navigation
 * - Active state highlighting
 */
export const TopNavLayoutEnhanced: React.FC<TopNavLayoutEnhancedProps> = ({
  children,
  userInfo,
  showBreadcrumbs = true,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { mutate: logout } = useLogout();

  // Auth validation - check every 1 minute
  const { isExpired, countdown, handleConfirm } = useAuthValidation({
    interval: 60000, // 1 minute
    enabled: true,
  });

  // Navigation state (모바일 Drawer + 로그아웃 다이얼로그)
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileExpandedMenu, setMobileExpandedMenu] = useState<string | null>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // 기능 권한 체크 (permissionKey 기반)
  const { hasPermission: hasFunctionPermission } = usePermission();

  // permissionKey 기반으로 네비게이션 필터링 (모바일 사이드바용)
  const filteredNavConfig = navigationConfig
    .filter(item => !item.permissionKey || hasFunctionPermission(item.permissionKey))
    .map(item => {
      if (!item.submenu) return item;
      return {
        ...item,
        submenu: item.submenu.filter(
          sub => !sub.permissionKey || hasFunctionPermission(sub.permissionKey)
        ),
      };
    });

  // Check if menu item is active
  const isActiveMenu = (item: MenuItem): boolean => {
    // Exact match
    if (location.pathname === item.path) {
      return true;
    }

    // Check if any submenu item is active (for parent highlighting)
    if (item.submenu) {
      return item.submenu.some(sub => location.pathname.startsWith(sub.path));
    }

    // Prefix match for nested routes
    return location.pathname.startsWith(item.path + '/');
  };

  // Generate breadcrumbs
  const generateBreadcrumbs = (): Breadcrumb[] => {
    const breadcrumbs: Breadcrumb[] = [{ label: '홈', path: '/' }];

    // Find current menu item
    for (const item of navigationConfig) {
      if (location.pathname.startsWith(item.path)) {
        breadcrumbs.push({ label: item.label, path: item.path });

        // Check submenu
        if (item.submenu) {
          const activeSubmenu = item.submenu.find(sub => location.pathname === sub.path);
          if (activeSubmenu) {
            breadcrumbs.push({
              label: activeSubmenu.label,
              path: activeSubmenu.path,
            });
          }
        }

        // Special handling for /transmission/epod-register
        // navigationConfig 의 submenu 는 정확 일치로만 매칭되는데 등록 페이지는 메뉴에 없다.
        // E-POD(목록)를 거쳐 들어온 하위 화면이므로 그 경로를 중간에 끼워 넣는다.
        if (location.pathname === '/transmission/epod-register') {
          breadcrumbs.push({ label: 'E-POD', path: '/transmission/detail' });
          breadcrumbs.push({ label: '등록', path: '/transmission/epod-register' });
        }

        // Special handling for /dispatch/create
        if (location.pathname === '/dispatch/create') {
          breadcrumbs.push({ label: '배차등록', path: '/dispatch/create' });
        }

        // Special handling for /dispatch/:id (detail page)
        if (location.pathname.match(/^\/dispatch\/\d+$/)) {
          breadcrumbs.push({ label: '배차정보', path: location.pathname });
        }

        // Special handling for /dispatch/:id/edit (edit page)
        if (location.pathname.match(/^\/dispatch\/\d+\/edit$/)) {
          breadcrumbs.push({ label: '배차수정', path: location.pathname });
        }

        // Special handling for /pickup/:id (detail page)
        if (location.pathname.match(/^\/pickup\/\d+$/)) {
          breadcrumbs.push({ label: '상세정보', path: location.pathname });
        }

        // Special handling for /pickup/:id/edit (edit page)
        if (location.pathname.match(/^\/pickup\/\d+\/edit$/)) {
          breadcrumbs.push({ label: '픽업수정', path: location.pathname });
        }

        // Special handling for /delivery/:id (detail page)
        if (location.pathname.match(/^\/delivery\/\d+$/)) {
          breadcrumbs.push({ label: '배송 상세정보', path: location.pathname });
        }

        // Special handling for /delivery/:id/edit (edit page)
        if (location.pathname.match(/^\/delivery\/\d+\/edit$/)) {
          breadcrumbs.push({ label: '배송수정', path: location.pathname });
        }

        break;
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Mobile submenu item click
  const handleSubmenuClick = (item: MenuItem) => {
    navigate(item.path);
    setMobileDrawerOpen(false);
  };

  // Mobile drawer handlers
  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileMenuToggle = (path: string) => {
    setMobileExpandedMenu(mobileExpandedMenu === path ? null : path);
  };

  // Header event handlers
  const handleLogoClick = () => {
    const user = authService.getStoredUser();
    if (user?.userType === 'PARTNER') {
      navigate('/status');
    } else {
      navigate('/');
    }
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    logout();
    setLogoutDialogOpen(false);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  // Render desktop navigation
  const renderDesktopNav = () => (
    <>
      {/* Main Menu - Full Width */}
      <Box
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #d9d9d9',
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '100vw',
          height: '50px', // MainMenu 높이와 동일하게 설정
          overflow: 'visible', // 스크롤바 제거 및 서브메뉴 표시
          position: 'relative',
          zIndex: 1,
          flexShrink: 0, // 높이 축소 방지
        }}
      >
        <MainMenu />
      </Box>
    </>
  );

  // Render mobile drawer navigation
  const renderMobileNav = () => (
    <>
      {/* Mobile Menu Button */}
      <Box
        sx={{
          display: { xs: 'flex', lg: 'none' },
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: theme.zIndex.appBar + 1,
        }}
      >
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            color: 'white',
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          {mobileDrawerOpen ? <CloseIcon /> : <MenuIcon />}
        </IconButton>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ overflow: 'auto', pt: 2 }}>
          {/* Drawer Header */}
          <Box
            sx={{
              px: 2,
              py: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              메뉴
            </Typography>
            <IconButton onClick={handleDrawerToggle} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          {/* Navigation List */}
          <List>
            {filteredNavConfig.map(item => {
              const isActive = isActiveMenu(item);
              const hasSubmenu = Boolean(item.submenu);
              const isExpanded = mobileExpandedMenu === item.path;

              return (
                <React.Fragment key={item.path}>
                  <ListItemButton
                    onClick={() => {
                      if (hasSubmenu) {
                        handleMobileMenuToggle(item.path);
                      } else {
                        navigate(item.path);
                        setMobileDrawerOpen(false);
                      }
                    }}
                    sx={{
                      backgroundColor:
                        isActive && !hasSubmenu ? 'rgba(0, 63, 95, 0.08)' : 'transparent',
                      borderLeft:
                        isActive && !hasSubmenu
                          ? `4px solid ${theme.palette.primary.main}`
                          : '4px solid transparent',
                    }}
                  >
                    {item.icon && (
                      <ListItemIcon
                        sx={{
                          color: isActive ? theme.palette.primary.main : 'text.secondary',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 400,
                      }}
                    />
                    {hasSubmenu && (isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                  </ListItemButton>

                  {/* Submenu Collapse */}
                  {hasSubmenu && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.submenu?.map(subItem => {
                          const isSubmenuActive = location.pathname === subItem.path;

                          return (
                            <ListItemButton
                              key={subItem.path}
                              onClick={() => handleSubmenuClick(subItem)}
                              sx={{
                                pl: 4,
                                backgroundColor: isSubmenuActive
                                  ? 'rgba(0, 63, 95, 0.08)'
                                  : 'transparent',
                                borderLeft: isSubmenuActive
                                  ? `4px solid ${theme.palette.primary.main}`
                                  : '4px solid transparent',
                              }}
                            >
                              {subItem.icon && (
                                <ListItemIcon
                                  sx={{
                                    minWidth: 32,
                                    color: isSubmenuActive
                                      ? theme.palette.primary.main
                                      : 'text.secondary',
                                  }}
                                >
                                  {React.cloneElement(subItem.icon, { fontSize: 'small' })}
                                </ListItemIcon>
                              )}
                              <ListItemText
                                primary={subItem.label}
                                primaryTypographyProps={{
                                  fontSize: '0.875rem',
                                  fontWeight: isSubmenuActive ? 600 : 400,
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      </Drawer>
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Header
        userName={userInfo?.full_name || '이드림'}
        onLogoClick={handleLogoClick}
        onSettingsClick={handleSettingsClick}
        onLogoutClick={handleLogoutClick}
        onProfileClick={handleProfileClick}
        position="sticky"
        isAuthenticated={true}
      />

      {/* Mobile Navigation */}
      {renderMobileNav()}

      {/* Main Content Area */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          overflowY: 'hidden',
          minHeight: 0,
        }}
      >
        {/* Desktop Navigation (MainMenu) */}
        {renderDesktopNav()}

        {/* Breadcrumbs - Below Menu */}
        {showBreadcrumbs && breadcrumbs.length > 1 && (
          <Box
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
              py: 1,
              display: 'block',
              width: '100%',
              maxWidth: '100vw',
              flexShrink: 0,
            }}
          >
            <Box sx={{ px: { xs: 2, sm: 3 } }}>
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
              >
                {breadcrumbs.map((crumb, index) => {
                  const isLast = index === breadcrumbs.length - 1;

                  return isLast ? (
                    <Typography
                      key={crumb.path}
                      color="text.primary"
                      fontSize="0.875rem"
                      fontWeight={600}
                    >
                      {crumb.label}
                    </Typography>
                  ) : (
                    <Link
                      key={crumb.path}
                      underline="hover"
                      color="inherit"
                      href={crumb.path}
                      onClick={e => {
                        e.preventDefault();
                        navigate(crumb.path);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                      }}
                    >
                      {index === 0 && <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />}
                      {crumb.label}
                    </Link>
                  );
                })}
              </Breadcrumbs>
            </Box>
          </Box>
        )}

        {/* Scrollable Body Content Area */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            backgroundColor: theme.palette.background.default,
            width: '100%',
            maxWidth: '100vw',
            overflowX: 'hidden',
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          {/* Page Content */}
          {children}
        </Box>
      </Box>

      {/* Footer - Figma Design v03 - Always at bottom */}
      <Footer />

      {/* Logout Confirmation Dialog - Figma Design */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            border: '1px solid #c4c4c4',
            minWidth: '321px',
            maxWidth: '400px',
            padding: 0,
          },
        }}
      >
        <DialogTitle
          id="logout-dialog-title"
          sx={{
            textAlign: 'center',
            fontSize: '16px',
            fontWeight: 400,
            color: '#0e0e0e',
            borderBottom: '1px solid #e6e6e6',
            padding: '15px 24px',
            margin: 0,
          }}
        >
          로그아웃
        </DialogTitle>
        <DialogContent
          sx={{
            padding: '40px 24px 0 !important',
            textAlign: 'center',
            margin: 0,
          }}
        >
          <DialogContentText
            id="logout-dialog-description"
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              color: '#0e0e0e',
              whiteSpace: 'pre-line',
              margin: 0,
              padding: 0,
            }}
          >
            정말 로그아웃 하시겠습니까?
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            padding: '40px 24px 24px',
            gap: '8px',
            margin: 0,
          }}
        >
          <Button
            onClick={handleLogoutCancel}
            sx={{
              width: '100px',
              height: '40px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 700,
              color: '#0e0e0e',
              backgroundColor: '#ffffff',
              border: '1px solid #c4c4c4',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            아니오
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            autoFocus
            sx={{
              width: '100px',
              height: '40px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 700,
              backgroundColor: '#003f5f',
              border: '1px solid #00324c',
              '&:hover': {
                backgroundColor: '#00324c',
              },
            }}
          >
            예
          </Button>
        </DialogActions>
      </Dialog>

      {/* Session Expired Dialog - 5 second countdown */}
      <SessionExpiredDialog open={isExpired} countdown={countdown} onConfirm={handleConfirm} />
    </Box>
  );
};

export default TopNavLayoutEnhanced;
